/**
 * Chat Dispatcher
 * Simple module-level event dispatcher for opening chat
 * Allows external components to trigger chat opening
 * Production-grade implementation with error handling
 */

type ChatOpenListener = () => void;

let openChatListener: ChatOpenListener | null = null;

/**
 * Register chat open listener
 * Called by LiveChatWidget on mount
 */
export function registerChatOpenListener(listener: ChatOpenListener): void {
  if (typeof listener !== 'function') {
    console.error('Chat dispatcher: listener must be a function');
    return;
  }
  openChatListener = listener;
}

/**
 * Unregister chat open listener
 * Called by LiveChatWidget on unmount
 */
export function unregisterChatOpenListener(): void {
  openChatListener = null;
}

/**
 * Open chat
 * Can be called from any component
 * Returns boolean indicating if the chat was opened successfully
 */
export function openChat(): boolean {
  if (openChatListener && typeof openChatListener === 'function') {
    try {
      openChatListener();
      return true;
    } catch (error) {
      console.error('Chat dispatcher: Error opening chat', error);
      return false;
    }
  } else {
    console.warn('Chat dispatcher: No listener registered. Chat widget may not be mounted yet.');
    return false;
  }
}

/**
 * Check if chat dispatcher is ready
 */
export function isChatDispatcherReady(): boolean {
  return openChatListener !== null && typeof openChatListener === 'function';
}

